import { trackEvent } from 'user-analytics';
import { SEAT_TAPPED, SMART_SEATS_SELECTION } from 'constants/TrackEvents';
import errorMonitoring from '@/errorMonitoring';
import { buildAutopilotTappedSeatProps } from './autopilot';

export { SMART_SEATS_SELECTION } from 'constants/TrackEvents';

/**
 * Tracks one complete smart-seat reservation result.
 * Returning the client value lets the booking owner isolate rejected thenables.
 * @param {object} payload Event-specific smart selection properties.
 * @returns {*} Analytics client result.
 */
export function trackSmartSeatsSelection(payload) {
  return trackEvent(SMART_SEATS_SELECTION, payload);
}

/**
 * Tracks when the adjacent dialog is opened.
 */
export function trackOpenAdjacentDialog() {
  trackEvent('Adjacent Dialog Opened');
}

/**
 * Tracks when the adjacent dialog is closed.
 */
export function trackCloseAdjacentDialog() {
  trackEvent('Adjacent Dialog Closed');
}

/**
 * Tracks when user taps a seat in seat diagram.
 * @param {Object} payload - Tap event payload.
 * @param {number} payload.seatNumber - Tapped seat number.
 * @param {number} payload.seatFloor - Tapped seat floor.
 * @param {string} payload.fareType - Tapped seat fare type (seatFareReference or seatFareDescription).
 * @param {number} payload.fareAmount - Tapped seat fare amount.
 * @param {number} payload.tapIndex - Tap index for current seat diagram session.
 * @param {string} payload.action - Selected or Deselected.
 * @param {string} payload.screen - Schedule screen or Checkout breadcrumb.
 * @param {string} payload.departureRoute - Departure route.
 * @param {Object} [payload.pricing] - Displayed leg pricing block for AutoPilot context.
 * @param {Object} [payload.seat] - Tapped seat object for AutoPilot context.
 */
export function trackSeatTapped(payload) {
  const { seatNumber, seatFloor, fareType, fareAmount, tapIndex, action, screen, departureRoute } =
    payload;

  // Bounded, non-fatal AutoPilot enrichment: a failure here must never block the tap event.
  let autopilotProps = {};
  try {
    const { pricing, seat } = payload;
    autopilotProps = buildAutopilotTappedSeatProps({ pricing, seat });
  } catch (error) {
    autopilotProps = {};
    errorMonitoring.notify({ error, info: { context: 'trackSeatTapped.autopilot' } });
  }

  trackEvent(SEAT_TAPPED, {
    'Seat number': Number(seatNumber) || 0,
    'Seat floor': Number(seatFloor) || 1,
    'Fare type': fareType || 'General',
    'Fare amount': Number(fareAmount) || 0,
    'Tap index': Number(tapIndex) || 0,
    'Action': action || '',
    'Screen': screen || '',
    'Departure route': departureRoute || '',
    ...autopilotProps,
  });
}
